import { Component, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { F4626Service } from './f-4626.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { safeEvalFormula } from '../../../shared/_utils/safe-eval-formula';

import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

declare const _: any;

@Component({
    selector: 'gtw-f-4626-ce',
    templateUrl: './f-4626.component.html',
    styleUrls: ['./f-4626.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F4626Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private initialized = false;

  // Component properties
  rowData: any = {};
  filterParams: any = {};
  baseUrls: any = {};
  appService: any = { USER_SETTINGS: {}, SERVER_CONFIG: {} };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  // STANDARD COMPONENT PROPERTIES (MANDATORY from superprompt)
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = false;

  // Entity data properties
  entityList: { key: string; value: any }[] = [];
  leId: string = '';
  cdr_no: string = '';
  leType: string = '';

  // F4626 specific properties
  f4626Header: any = {};
  f4626List: any[] = [];
  f4626OriginalData: any[] = [];
  f4626FormData: any[] = [];
  f4626ColumnHeader: any[] = [];
  f4626FinalData: any[] = [];
  unAllocatedAmt: number = 0;

  // Data loading optimization
  private dataLoadingStates = {
    entityData: false,
    formData: false
  };

  constructor(
    private f4626Service: F4626Service,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.initialized = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.initialized = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseUrls = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    this.appService = { USER_SETTINGS: header.userSettingsData || {}, SERVER_CONFIG: header.serverConfigData || {} };
    this.USER_SETTINGS = this.appService.USER_SETTINGS;
    this.SERVER_CONFIG = this.appService.SERVER_CONFIG;
    this.cdr.markForCheck();
  }

  /**
   * Called from each @Input setter and ngOnInit.
   * Only proceeds when ALL required inputs (rowData, filterParams, GENERAL_CONFIG) are available.
   * Runs init() exactly once via the `initialized` guard.
   */
  private tryInit(): void {
    if (this.initialized) return;
    const hasRow = this.rowData && Object.keys(this.rowData).length > 0;
    const hasFilter = this.filterParams && Object.keys(this.filterParams).length > 0;
    const hasConfig = this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0;

    if (hasRow) {
      this.setupEntityData();
    }
    if (hasRow && hasFilter && hasConfig) {
      this.initialized = true;
      this.getF4626Data();
    } else {
      this.showFormLoader = true;
    }
  }

  init(): void {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.setupEntityData();
        this.getF4626Data();
      }
    } catch (e) {
      console.error('Error in F4626 init:', e);
      this.showFormLoader = false;
    }
  }

  private setupEntityData(): void {
    // CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS || this.rowData.CDR_NO;
    this.leType = this.rowData.LE_TYPE;

    // MANDATORY ORDER: Tax Year, LEID, LE Name
    this.entityList = [
      { key: "Tax Year", value: this.rowData.TAX_YEAR },
      { key: "LEID", value: this.rowData.LEID },
      { key: "LE Name", value: this.rowData.LE_NAME }
    ];

    this.dataLoadingStates.entityData = true;
    this.checkAllDataLoaded();
  }

  checkAllDataLoaded(): void {
    // Only hide main loader when ALL required data is loaded
    const allLoaded = Object.values(this.dataLoadingStates).every(state => state);
    if (allLoaded) {
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  getF4626Data(): void {
    this.showFormLoader = true;
    this.cdr.detectChanges();

    const logged_in_user = this.USER_SETTINGS?.user?.sso_id || this.globalService?.globalParams?.sso_id || '';

    this.f4626Service.fetchF4626Details(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      logged_in_user,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.jsonObject && data.jsonObject.length > 0) {
          this.processF4626Data(data.jsonObject);
        } else {
          this._snackBar.open('No data found!', 'Close', {
            duration: 4000,
            panelClass: ['warning-snackbar']
          });
        }
        
        this.showFormLoader = false;
        this.dataLoadingStates.formData = true;
        this.checkAllDataLoaded();
        this.cdr.markForCheck();
      },
      (error: any) => {
        this._snackBar.open('Failed to load data.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  private processF4626Data(data: any[]): void {
    // Extract header
    this.f4626Header = data[0];
    this.f4626List = data.slice(1);

    // Group by LINE_ORDER - matching original AngularJS logic
    const o = this.f4626List.reduce((a: any, b: any) => {
      a[b.LINE_ORDER] = a[b.LINE_ORDER] || [];
      a[b.LINE_ORDER].push(b);
      return a;
    }, {});

    const a = Object.keys(o).map(function (k) {
      return { ROW_TYPE: o[k][0].ROW_TYPE, LINE_NO: o[k][0].LINE_NO, DELETE_ROWS: [], Line_data: o[k] };
    });

    this.f4626FormData = a;

    // Process currency fields - convert to integer
    for (let i = 0; i < this.f4626FormData.length; i++) {
      if (this.f4626FormData[i].ROW_TYPE == 'DT') {
        for (let j = 0; j < this.f4626FormData[i].Line_data.length; j++) {
          if (this.f4626FormData[i].Line_data[j].IS_EDITABLE == 'Y' &&
            this.f4626FormData[i].Line_data[j].EDIT_TYPE == 'currency' &&
            this.f4626FormData[i].Line_data[j].DATA_TYPE == 'number') {
            this.f4626FormData[i].Line_data[j].ATTRIB_VALUE = parseInt(this.f4626FormData[i].Line_data[j].ATTRIB_VALUE) || 0;
          }
        }
      }
    }
    
    // Save original data for comparison
    this.f4626OriginalData = _.cloneDeep(this.f4626FormData);
    
    this.cdr.detectChanges();
  }

  valuechanged(parent_index: number, occurrence: any, line_no: string, index: number): void {

    for (let i = 0; i < this.f4626FormData.length; i++) {
      if (i == parent_index && this.f4626FormData[i].Line_data[index] != null && this.f4626FormData[i].Line_data[index] != undefined) {
        this.f4626FormData[i].Line_data[index].IS_CHANGED_FLAG = "Y";
      }
    }
    
  }

  updateAmount(index: number, line_no: string): void {
    
    if ((this.f4626FormData[index].ROW_TYPE == 'DT' || this.f4626FormData[index].ROW_TYPE == 'DD' || this.f4626FormData[index].ROW_TYPE == 'LH') && 
         this.f4626FormData[index].LINE_NO == line_no) {
      for (let j = 0; j < this.f4626FormData[index].Line_data.length; j++) {
        if (this.f4626FormData[index].Line_data[j].ATTRIB_CALC_FORMULA != null) {
          const line_data_values = this.f4626FormData[index].Line_data;
          this.f4626FormData[index].Line_data[j].ATTRIB_VALUE = this.calcLineFormula(
            this.f4626FormData[index].Line_data[j].ATTRIB_CALC_FORMULA, 
            line_data_values
          );
          this.f4626FormData[index].Line_data[j].IS_CHANGED_FLAG = "Y";
          this.f4626FormData[index].Line_data[j].HAS_ERROR = "N";
        }
      }
    }
    
    this.calcFinalAmounts();
  }

  calcLineFormula(formula: string, line_data_values: any[]): number {
    
    try {
      const lineFormulaArray = formula.split(/[+\-*/]/);
      
      for (let i = 0; i < lineFormulaArray.length; i++) {
        if (lineFormulaArray[i].indexOf("(") > -1 && lineFormulaArray[i].indexOf(")") > -1) {
          lineFormulaArray[i] = lineFormulaArray[i].replace(/"/g, "").replace(/'/g, "").replace(/\(|\)/g, "");
          const currentTabAttribVal = line_data_values.filter((item: any) => 
            item.ATTRIB_SHORT_CODE === lineFormulaArray[i].trim()
          );
          
          if (currentTabAttribVal && currentTabAttribVal.length > 0) {
            const lineAmt = parseInt(currentTabAttribVal[0].ATTRIB_VALUE) || 0;
            if (parseInt(lineAmt.toString()) > 0) {
              formula = formula.replace("(" + lineFormulaArray[i].trim() + ")", lineAmt.toString());
            } else {
              formula = formula.replace("(" + lineFormulaArray[i].trim() + ")", "(" + lineAmt + ")");
            }
          }
        }
      }
      
      const result = safeEvalFormula(formula, null, 'f-4626.calcLineFormula');
      if (result != null && parseInt(String(result)) === parseInt(String(result))) {
        console.log(result);
        return result;
      } else {
        return 0;
      }
    } catch (e) {
      console.error('Error calculating line formula:', e);
      return 0;
    }
  }

  calcFinalAmounts(): void {
    for (let i = 0; i < this.f4626FormData.length; i++) {
      for (let j = 0; j < this.f4626FormData[i].Line_data.length; j++) {
        if (this.f4626FormData[i].Line_data[j].ATTRIB_CALC_FORMULA != null) {
          const final_amt = this.calc_final_amt(this.f4626FormData[i].Line_data[j].ATTRIB_CALC_FORMULA);
          if (this.f4626FormData[i].Line_data[j].ATTRIB_VALUE != final_amt) {
            this.f4626FormData[i].Line_data[j].ATTRIB_VALUE = final_amt;
            this.f4626FormData[i].Line_data[j].IS_CHANGED_FLAG = "Y";
          }
        }
      }
    }
  }

  calc_final_amt(formula: string): number {
    try {
      const lineFormulaArray = formula.split(/[+\-*/]/);
      
      for (let i = 0; i < lineFormulaArray.length; i++) {
        let lineAmt = 0;
        if (lineFormulaArray[i].indexOf("(") > -1 && lineFormulaArray[i].indexOf(")") > -1) {
          lineFormulaArray[i] = lineFormulaArray[i].replace(/"/g, "").replace(/'/g, "").replace(/\(|\)/g, "");
          
          for (let k = 0; k < this.f4626FormData.length; k++) {
            const row_data = this.f4626FormData[k].Line_data;
            const currentTabAttribVal = row_data.filter((item: any) => 
              item.ATTRIB_SHORT_CODE === lineFormulaArray[i].trim()
            );
            
            if (null != currentTabAttribVal && currentTabAttribVal.length > 0) {
              lineAmt = lineAmt + (parseInt(currentTabAttribVal[0].ATTRIB_VALUE) || 0);
            }
          }
          
          if (parseInt(lineAmt.toString()) > 0) {
            formula = formula.replace("(" + lineFormulaArray[i].trim() + ")", lineAmt.toString());
          } else {
            formula = formula.replace("(" + lineFormulaArray[i].trim() + ")", "(" + lineAmt + ")");
          }
        }
      }

      const result_val = safeEvalFormula(formula, null, 'f-4626.calcFinalAmt');
      if (result_val != null && parseInt(String(result_val)) === parseInt(String(result_val))) {
        const rounded = Math.round(result_val);
        console.log(rounded);
        return rounded;
      } else {
        return 0;
      }
    } catch (e) {
      console.error('Error calculating final amount:', e);
      return 0;
    }
  }

  save(): void {
    this.isSaveClicked = true;
    this.crudLoading = true;

    // Check for unsaved changes
    if (_.isEqual(this.f4626FormData, this.f4626OriginalData)) {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.cancel();
      return;
    }

    // Prepare final data
    this.f4626FinalData = _.cloneDeep(this.f4626FormData);
    const finalData: any[] = [];

    for (let x = 0; x < this.f4626FinalData.length; x++) {
      if (this.f4626FinalData[x].Line_data.length != null && this.f4626FinalData[x].Line_data.length != undefined) {
        for (let j = 0; j < this.f4626FinalData[x].Line_data.length; j++) {
          finalData.push(this.f4626FinalData[x].Line_data[j]);
        }
      }
    }

    const logged_in_user = this.USER_SETTINGS?.user?.sso_id || this.globalService?.globalParams?.sso_id || '';
    const clob_settings = [{
      sso_id: logged_in_user,
      screen_key: this.globalService?.globalParams?.screen_key || this.filterParams?.screen_key || ''
    }];

    this.f4626Service.saveForm4626(
      this.GENERAL_CONFIG.base_url,
      finalData,
      clob_settings,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.data && response.data.errorMessage && response.data.errorMessage != 'null') {
          this._snackBar.open(response.data.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Form 4626 Details are successfully updated.', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          // SUCCESS: Emit STRING 'refresh' (NOT object)
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this._snackBar.open('Failed to save data.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.cdr.markForCheck();
      }
    );
  }

  reset() {
    // Restore original values from cached data instead of making a service call
    this.f4626FormData = _.cloneDeep(this.f4626OriginalData);
    this.cdr.detectChanges();
  }

  cancel(): void {
    if (_.isEqual(this.f4626FormData, this.f4626OriginalData)) {
      // CRITICAL: Use EventEmitter for AngularJS modal dismissal
      this.requestGridUpdate.emit('cancel');
    } else {
      if (this.crudLoading) {
        this._snackBar.open('The transaction is still in progress', 'Close', {
          duration: 4000,
          panelClass: ['info-snackbar']
        });
      } else if (confirm("Sales of Business Property Information is not saved. Do you want to close Form without saving changes?")) {
        this.requestGridUpdate.emit('cancel');
      }
    }
  }

  // Radio button handler for Yes/No inputs
  onRadioChange(index: number, value: boolean, row: any): void {
    row.value = value;
    row.IS_CHANGED_FLAG = 'Y';
  }
}

